export interface AccountabilityLike {
  code?: string | number | null;
  name?: string | null;
}

/** Vuetify internal list item; `raw` is whatever was passed in `items`. */
interface FilterableItem {
  raw?: Record<string, unknown>;
}

export const normalize = (value: unknown): string =>
  String(value ?? "").normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

/**
 * True when the name is the code, or opens with the code and an explicit separator
 * ("5030 - Equipos"). A separator is required so a code that merely prefixes the name
 * is never dropped: "601" vs "601.84.23 Gastos", "OP" vs "OP-02 Ventas", "GALGO" vs
 * "Galgo Chile" all keep their code.
 */
const nameCarriesCode = (name: string, code: string): boolean => {
  const trimmed = name.trim();
  const lowerName = trimmed.toLowerCase();
  const lowerCode = code.toLowerCase();
  if (lowerName === lowerCode) return true;
  if (!lowerName.startsWith(lowerCode)) return false;
  return /^\s*[-–—|:]\s/.test(trimmed.slice(code.length));
};

/** The code to render before the name, or null when the name already carries it. */
export const accountabilityCodePrefix = (
  item: AccountabilityLike | null | undefined,
): string | null => {
  // Not a truthiness check: a numeric code of 0 would drop the prefix.
  if (item == null || item.code == null || item.code === "") return null;
  const code = String(item.code);
  return nameCarriesCode(String(item.name ?? ""), code) ? null : code;
};

/**
 * Display label for a cost center / accounting account: "601.84.23 | Gastos".
 * Masters whose name already carries the code render the name alone.
 */
export const accountabilityLabel = (item: AccountabilityLike | null | undefined): string => {
  const name = String(item?.name ?? "");
  const code = accountabilityCodePrefix(item);
  return code === null ? name : `${code} | ${name}`;
};

/** Matches the typed text against code and name, ignoring case and diacritics. */
export const customAccountabilityFilter = (
  value: string,
  search: string,
  item?: FilterableItem,
): boolean => {
  const query = normalize(search);
  return normalize(value).includes(query) || normalize(item?.raw?.code).includes(query);
};

/**
 * Accountability ids reach the front with inconsistent types: the `result` of an AI classification is
 * free JSON, so a writer can store `"38"` where the catalog carries `38`. A strict compare there
 * silently yields an empty list, which reads exactly like a category with no subcategories.
 */
const toId = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const sameId = (a: unknown, b: unknown): boolean => {
  const left = toId(a);
  return left !== null && left === toId(b);
};

export const subcategoriesOfCategory = <T extends { category_id?: number }>(
  subcategories: T[],
  categoryId: unknown,
): T[] => {
  const id = toId(categoryId);
  return id === null ? [] : subcategories.filter((s) => sameId(s.category_id, id));
};
